import React, { useState, useEffect } from 'react';
import { 
  View, 
  StyleSheet, 
  ScrollView, 
  Pressable, 
  Image,
  ImageBackground,
  ActivityIndicator,
  Platform,
  StatusBar
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LinearGradient } from 'expo-linear-gradient';
import { auth } from '../../config/firebase';
import { PrivateProfileService } from '../../services/privateProfileService';
import { Text, Theme, InsightListItem, Button, MarkdownRenderer } from '../../components/ui';
import { FireBook, FireInsight } from '../../models/FirestoreModels';
import { Ionicons } from '@expo/vector-icons';
import { useBookDetail } from '../../hooks/useBookDetail';
import { placeholders } from '../../assets/placeholders';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useLogging } from '../../contexts/LoggingContext';
import { useSubscription } from '../../hooks/useSubscription';
import { DailyFreeBookService } from '../../services/dailyFreeBookService';

type BookDetailScreenProps = {
  navigation: StackNavigationProp<any>;
  route: any;
};

const BookDetailScreen: React.FC<BookDetailScreenProps> = ({ navigation, route }) => {
  const { bookId, languageCode = 'en' } = route.params || {};
  const { book, insights, loading, error } = useBookDetail(bookId, languageCode);
  const [completedInsights, setCompletedInsights] = useState<Set<string>>(new Set());
  const [recommendationReason, setRecommendationReason] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);
  const { loadBookAudio } = useAudioPlayer();
  const { logBookOpen } = useLogging();
  const { hasContentAccess } = useSubscription();
  const [isFreeToday, setIsFreeToday] = useState(false);
  const [checkingFree, setCheckingFree] = useState(true);
  // Log book opened (once when book data is loaded)
  // This also serves as the screen view - no need for separate screen tracking
  useEffect(() => {
    if (book) {
      logBookOpen(bookId, book.title, 'book_detail_screen');
    }
  }, [book, bookId, logBookOpen]);

  // Load recommendation reason, completed insights, and favorite status
  useEffect(() => {
    if (!bookId || !auth.currentUser) return;
    
    const loadUserData = async () => {
      try {
        const result = await PrivateProfileService.getProfile(auth.currentUser!.uid);
        if (result.success && result.data) {
          const cleanBookId = bookId.includes('_') ? bookId.split('_')[0] : bookId;
          const bookProgress = result.data.booksProgress[cleanBookId];
          
          if (bookProgress?.recommendationReason) {
            setRecommendationReason(bookProgress.recommendationReason);
          }
          
          // Check completed insights from insightsProgress
          if (result.data.insightsProgress) {
            const completedIds = Object.values(result.data.insightsProgress)
              .filter(p => p.bookId === cleanBookId && p.isCompleted)
              .map(p => p.id);
            setCompletedInsights(new Set(completedIds));
          }
          
          // Set favorite status
          setIsFavorite(bookProgress?.isFavorite || false);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };

    loadUserData();
  }, [bookId]);

  // Check if this book is the daily free book
  useEffect(() => {
    let mounted = true;
    const loadFree = async () => {
      try {
        const isFree = await DailyFreeBookService.isBookFreeToday(bookId, languageCode);
        if (mounted) setIsFreeToday(isFree);
      } catch (error) {
        console.warn('[BookDetail] Failed to check daily free', error);
      } finally {
        if (mounted) setCheckingFree(false);
      }
    };
    loadFree();
    return () => {
      mounted = false;
    };
  }, [bookId, languageCode]);
  
  const handleToggleFavorite = async () => {
    if (!auth.currentUser || togglingFavorite) return;
    
    setTogglingFavorite(true);
    const newFavoriteStatus = !isFavorite;
    
    // Optimistically update UI
    setIsFavorite(newFavoriteStatus);
    
    try {
      const cleanBookId = bookId.includes('_') ? bookId.split('_')[0] : bookId;
      const result = await PrivateProfileService.toggleBookFavorite(
        auth.currentUser.uid,
        cleanBookId,
        newFavoriteStatus
      );
      
      if (result.success) {
        console.log(`[BookDetail] Book ${newFavoriteStatus ? 'added to' : 'removed from'} favorites`);
      } else {
        // Revert on error
        setIsFavorite(!newFavoriteStatus);
        console.error('[BookDetail] Failed to toggle favorite:', result.error);
      }
    } catch (error) {
      // Revert on error
      setIsFavorite(!newFavoriteStatus);
      console.error('[BookDetail] Error toggling favorite:', error);
    } finally {
      setTogglingFavorite(false);
    }
  };

  const locked = !hasContentAccess && !isFreeToday;

  const handleInsightPress = (insight: FireInsight) => {
    if (locked) {
      navigation.navigate('Paywall' as never);
      return;
    }
    if (!book) return;
    const indexInInsights = insights.findIndex(i => i.id === insight.id);
    const hasIntroduction = !!book.introduction;
    const currentIndex = hasIntroduction ? indexInInsights + 1 : indexInInsights;
    
    navigation.replace('InsightDetail', { 
      insights,
      book,
      bookTitle: book?.title,
      bookCoverUrl: book?.coverImageUrl || book?.coverImageFullUrl,
      currentIndex,
      fromRoute: 'BookDetail',
    });
  };

  const handleStartLearning = async (autoPlayAudio: boolean = false) => {
    if (locked) {
      navigation.navigate('Paywall' as never);
      return;
    }
    if (!book || !insights.length) return;
    
    const { hasIntroduction } = await loadBookAudio(book, insights);
    
    if (hasIntroduction) {
      navigation.navigate('Introduction', { book, autoPlayAudio });
    } else if (insights.length > 0) {
      navigation.replace('InsightDetail', { 
        insights,
        book,
        bookTitle: book.title,
        bookCoverUrl: book.coverImageUrl || book.coverImageFullUrl,
        currentIndex: 0,
        fromRoute: 'BookDetail',
        autoPlayAudio,
      });
    }
  };

  const getInsightDuration = (insight: FireInsight): string | undefined => {
    if (!insight.audioSegments) return undefined;
    
    let totalSeconds = 0;
    // Common segment types to sum up
    const segmentTypes = ['story', 'actionSteps', 'introduction', 'summary', 'conclusion'];
    
    Object.entries(insight.audioSegments).forEach(([type, voices]) => {
      if (segmentTypes.includes(type) && voices) {
        // Use first available voice for duration calculation
        const voice = Object.values(voices)[0];
        if (voice?.duration) {
          totalSeconds += voice.duration;
        }
      }
    });
    
    if (totalSeconds === 0) return undefined;
    
    const minutes = Math.ceil(totalSeconds / 60);
    return `${minutes} min`;
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Theme.colors.primary} />
      </View>
    );
  }

  if (error || !book) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Unable to load book details</Text>
      </View>
    );
  }

  const readInsights = completedInsights.size;
  const totalInsights = insights.length;
  const progressPercent = totalInsights > 0 ? (readInsights / totalInsights) * 100 : 0;
  const nextUnreadInsight = insights.find(i => !completedInsights.has(i.id));

  const coverUrl = book.coverImageUrl || book.coverImageFullUrl || placeholders.bookCover;

  return (
    <View style={styles.container}>
      {/* Simplified Header */}
      <View style={styles.stickyHeader}>
        <Pressable style={styles.headerButton} onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color={Theme.colors.textPrimary} />
        </Pressable>
        <View style={{ flex: 1 }} />
        <Pressable 
          style={styles.headerButton} 
          onPress={handleToggleFavorite}
          disabled={togglingFavorite}
        >
          {togglingFavorite ? (
            <ActivityIndicator size="small" color={Theme.colors.textPrimary} />
          ) : (
            <Ionicons 
              name={isFavorite ? "bookmark" : "bookmark-outline"} 
              size={24} 
              color={isFavorite ? Theme.colors.primary : Theme.colors.textPrimary} 
            />
          )}
        </Pressable>
      </View>

      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Book Cover */}
        <View style={styles.coverContainer}>
          <Image 
            source={{ uri: coverUrl }}
            style={styles.bookCover}
            resizeMode="cover"
          />
        </View>

        {/* Summary Badge */}
        <View style={styles.summaryBadgeContainer}>
          <Text style={styles.summaryBadge}>SUMMARY</Text>
        </View>

        {/* Title & Author */}
        <View style={styles.titleContainer}>
          <Text style={styles.bookTitle}>{book.title}</Text>
          <Text style={styles.bookAuthor}>{book.author}</Text>
        </View>

        {/* Stats Row */}
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Ionicons name="list-outline" size={16} color={Theme.colors.primary} />
            <Text style={styles.statText}>{totalInsights} key points</Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons name="time-outline" size={16} color={Theme.colors.primary} />
            <Text style={styles.statText}>20 min</Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons name="bulb-outline" size={16} color={Theme.colors.primary} />
            <Text style={styles.statText}>{totalInsights} insights</Text>
          </View>
        </View>

        {/* AI Mentor Message Card */}
        {recommendationReason && (
          <View style={styles.mentorCard}>
            <View style={styles.mentorHeader}>
              <View style={styles.mentorInfo}>
                <Text style={styles.mentorTitle}>Your Mentor</Text>
                <View style={styles.aiBadge}>
                  <Ionicons name="sparkles" size={10} color={Theme.colors.textSecondary} />
                  <Text style={styles.aiText}>AI</Text>
                </View>
              </View>
            </View>
            <Text style={styles.mentorMessage}>{recommendationReason}</Text>
          </View>
        )}

        {/* What's Inside Section */}
        {book.biggestTakeaway && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>What's inside?</Text>
            <MarkdownRenderer 
              content={book.biggestTakeaway}
              fontSize={15}
              fontFamily={Theme.typography.fontFamily.regular}
            />
          </View>
        )}

        {/* Key Ideas List */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key ideas</Text>
          <View style={styles.insightsList}>
            {insights.map((insight, index) => {
              const isRead = completedInsights.has(insight.id);
              return (
                <Pressable 
                  key={insight.id}
                  style={styles.insightItem}
                  onPress={() => handleInsightPress(insight)}
                >
                  <View style={styles.insightNumber}>
                    {isRead ? (
                      <Ionicons name="checkmark-circle" size={20} color={Theme.colors.primary} />
                    ) : (
                      <Text style={styles.insightNumberText}>{index + 1}</Text>
                    )}
                  </View>
                  <Text style={[styles.insightTitle, isRead && styles.insightTitleRead]}>
                    {insight.title}
                  </Text>
                  <Ionicons name="chevron-forward" size={20} color={Theme.colors.textSecondary} />
                </Pressable>
              );
            })}
          </View>
        </View>

        {/* About Author Section */}
        {book.aboutAuthor && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>About {book.author}</Text>
            <MarkdownRenderer 
              content={book.aboutAuthor}
              fontSize={15}
              fontFamily={Theme.typography.fontFamily.regular}
            />
          </View>
        )}

        {/* Bottom padding for sticky footer */}
        <View style={{ height: 140 }} />
      </ScrollView>

      {/* Sticky Footer */}
      <View style={styles.stickyFooter}>
        {/* Action Buttons */}
        <View style={styles.footerActions}>
          <Pressable 
            style={styles.readButtonFooter}
            onPress={() => handleStartLearning(false)}
          >
            <Ionicons name="reader-outline" size={20} color={Theme.colors.textPrimary} />
            <Text style={styles.readButtonText}>Read</Text>
          </Pressable>
          <Pressable 
            style={styles.listenButtonFooter}
            onPress={() => handleStartLearning(true)}
          >
            <Ionicons name="headset-outline" size={20} color="#fff" />
            <Text style={styles.listenButtonText}>Listen</Text>
          </Pressable>
        </View>
      </View>

    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: Theme.spacing.xl,
  },
  errorText: {
    fontSize: 16,
    color: Theme.colors.error,
    textAlign: 'center',
  },
  stickyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Theme.spacing.lg,
    paddingBottom: Theme.spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.95)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.08)',
    ...Platform.select({
      ios: {
        paddingTop: 50,
      },
      android: {
        paddingTop: (StatusBar.currentHeight || 0) + Theme.spacing.sm,
      },
      web: {
        paddingTop: Theme.spacing.lg,
      },
    }),
  },
  headerButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: Theme.spacing.xl,
  },
  coverContainer: {
    alignItems: 'center',
    marginBottom: Theme.spacing.xl,
  },
  bookCover: {
    width: 200,
    height: 300,
    borderRadius: Theme.borderRadius.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 16,
    elevation: 8,
  },
  summaryBadgeContainer: {
    alignItems: 'center',
    marginBottom: Theme.spacing.md,
  },
  summaryBadge: {
    fontSize: 11,
    letterSpacing: 1.5,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.medium,
    textTransform: 'uppercase',
  },
  titleContainer: {
    marginBottom: Theme.spacing.xl,
  },
  bookTitle: {
    fontSize: 24,
    lineHeight: 32,
    fontFamily: Theme.typography.fontFamily.bold,
    fontWeight: '700',
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: Theme.spacing.sm,
  },
  bookAuthor: {
    fontSize: 16,
    color: Theme.colors.textSecondary,
    textAlign: 'center',
    fontFamily: Theme.typography.fontFamily.regular,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Theme.spacing.xl,
    marginBottom: Theme.spacing.xl,
    flexWrap: 'wrap',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  statText: {
    fontSize: 14,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.regular,
  },
  mentorCard: {
    backgroundColor: '#fff',
    borderRadius: Theme.borderRadius.xl,
    padding: Theme.spacing.xl,
    marginBottom: Theme.spacing.xl,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  mentorHeader: {
    marginBottom: Theme.spacing.md,
  },
  mentorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
  },
  mentorTitle: {
    fontSize: 14,
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.medium,
  },
  aiBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: Theme.spacing.sm,
    paddingVertical: 2,
    backgroundColor: Theme.colors.surfaceSecondary,
    borderRadius: Theme.borderRadius.sm,
  },
  aiText: {
    fontSize: 11,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.medium,
  },
  mentorMessage: {
    fontSize: 14,
    color: Theme.colors.textSecondary,
    lineHeight: 22,
    fontFamily: Theme.typography.fontFamily.regular,
  },
  section: {
    marginBottom: Theme.spacing.xxl,
  },
  sectionTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.bold,
    fontWeight: '700',
    color: Theme.colors.textPrimary,
    marginBottom: Theme.spacing.lg,
  },
  sectionText: {
    fontSize: 14,
    color: Theme.colors.textSecondary,
    lineHeight: 22,
    fontFamily: Theme.typography.fontFamily.regular,
  },
  insightsList: {
    gap: Theme.spacing.sm,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.md,
    padding: Theme.spacing.lg,
    backgroundColor: '#f8f9fa',
    borderRadius: Theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
  },
  insightNumber: {
    width: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  insightNumberText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.primary,
    fontWeight: '600',
  },
  insightTitle: {
    flex: 1,
    fontSize: 14,
    color: Theme.colors.textPrimary,
    fontFamily: Theme.typography.fontFamily.regular,
    lineHeight: 20,
  },
  insightTitleRead: {
    color: Theme.colors.textSecondary,
  },
  stickyFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
    paddingBottom: Platform.select({
      ios: 34,
      android: 16,
      web: 16,
    }),
  },
  footerActions: {
    flexDirection: 'row',
    paddingHorizontal: Theme.spacing.lg,
    paddingTop: Theme.spacing.md,
    paddingBottom: Theme.spacing.sm,
    gap: Theme.spacing.md,
  },
  readButtonFooter: {
    flex: 1,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Theme.spacing.xs,
    backgroundColor: '#fff',
    borderWidth: 1.5,
    borderColor: Theme.colors.textSecondary,
    borderRadius: Theme.borderRadius.lg,
  },
  readButtonText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
    fontWeight: '600',
  },
  listenButtonFooter: {
    flex: 2,
    height: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: Theme.spacing.xs,
    backgroundColor: Theme.colors.primary,
    borderRadius: Theme.borderRadius.lg,
  },
  listenButtonText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: '#fff',
    fontWeight: '600',
  },
});

export default BookDetailScreen;
